import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { useInfiniteDocuments, useToggleDocumentBookmark } from '../hooks/useDocuments'
import { useFolders } from '../hooks/useFolders'
import { DocumentSearchParams } from '../types/models'
import { DocumentTable } from '../components/documents/DocumentTable'
import { DocumentFilters } from '../components/documents/DocumentFilters'
import { FolderAclModal } from '../components/folders/FolderAclModal'
import { Layout } from '../components/layout/Layout'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export function DocumentListPage() {
  const navigate = useNavigate()
  const { folderId } = useParams<{ folderId?: string }>()
  const { user, firebaseUser } = useAuth()
  const { data: foldersData } = useFolders()
  const queryClient = useQueryClient()
  const toggleBookmarkMutation = useToggleDocumentBookmark()
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId || null)
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([])
  const [isDeleting, setIsDeleting] = useState(false)
  const [showBulkShareMenu, setShowBulkShareMenu] = useState(false)
  const [showBulkFolderMenu, setShowBulkFolderMenu] = useState(false)
  const [showFolderAcl, setShowFolderAcl] = useState(false)
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null)
  const [filters, setFilters] = useState<DocumentSearchParams>({
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  })

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useInfiniteDocuments(filters)

  // 無限スクロール。
  // ページ全体(window)ではなく .content-scroll 内がスクロールする構造のため、
  // リスト末尾の番兵要素を IntersectionObserver で監視する（スクロール親に依存しない）。
  const loadMoreSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect()
        loadMoreObserverRef.current = null
      }
      if (!node) return
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '800px 0px' }
      )
      observer.observe(node)
      loadMoreObserverRef.current = observer
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  // URLパラメータが変更されたときにselectedFolderIdを更新
  useEffect(() => {
    setSelectedFolderId(folderId || null)
  }, [folderId])

  const handleFiltersChange = (newFilters: DocumentSearchParams) => {
    setFilters(newFilters)
  }

  const handleFiltersReset = () => {
    setFilters({
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    })
  }

  const handleSort = (field: string) => {
    console.log('🔄 Sort clicked:', { field, currentFilters: filters })
    setFilters(prev => {
      const newOrder: 'asc' | 'desc' = prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc'
      const newFilters = {
        ...prev,
        sortBy: field as any,
        sortOrder: newOrder
      }
      console.log('🔄 New filters:', newFilters)
      return newFilters
    })
  }

  const handleBookmark = async (docId: string, isBookmarked: boolean) => {
    console.log('📋 DocumentListPage handleBookmark called:', { docId, isBookmarked })
    try {
      console.log('📋 Calling toggleBookmarkMutation...')
      await toggleBookmarkMutation.mutateAsync({ docId, isBookmarked })
      console.log('📋 toggleBookmarkMutation completed successfully')
    } catch (error) {
      console.error('Bookmark update failed:', error)
    }
  }

  const handleDocumentClick = (docId: string) => {
    navigate(`/documents/${docId}`)
  }

  // 全ページのドキュメントをフラット化
  const allDocuments = data?.pages.flatMap((page: any) => page?.items || []).filter(Boolean) ?? []
  const totalDocuments = data?.pages[0]?.pagination?.total ?? 0

  if (error) {
    console.error('Document list error:', error)
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
        <div className="empty-state">
          <div>
            <div className="empty-state-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="empty-state-title">エラーが発生しました</h2>
            <p className="empty-state-desc">{error.message}</p>
            <div className="card mt-4 text-left text-sm">
              <p><strong>ユーザー情報:</strong></p>
              <p>認証済み: {user ? '✓' : '✗'}</p>
              <p>組織ID: {user?.orgId || 'なし'}</p>
              <p>ロール: {user?.role || 'なし'}</p>
              <p>Firebase User: {user ? '✓' : '✗'}</p>
              <p><strong>API情報:</strong></p>
              <p>エンドポイント: {API_BASE}</p>
              <p>エミュレータ使用: {import.meta.env.VITE_USE_EMULATOR === 'true' ? '✓' : '✗'}</p>
            </div>
            <div className="mt-4 space-x-2">
              <button
                onClick={() => refetch()}
                className="btn btn-outline btn-sm"
              >
                再試行
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }


  const handleDocumentSelect = (documentId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedDocuments(prev => [...prev, documentId])
    } else {
      setSelectedDocuments(prev => prev.filter(id => id !== documentId))
    }
  }

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedDocuments(allDocuments.filter(doc => doc && doc.id).map(doc => doc.id))
    } else {
      setSelectedDocuments([])
    }
  }


  const handleDeleteSelected = async () => {
    if (selectedDocuments.length === 0) return

    const confirmed = window.confirm(`選択した${selectedDocuments.length}件の議事録を削除しますか？この操作は取り消せません。`)
    if (!confirmed) return

    setIsDeleting(true)
    try {
      const token = await firebaseUser?.getIdToken()

      // 並列で削除処理を実行
      await Promise.all(
        selectedDocuments.map(async (docId) => {
          const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/documents/${docId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (!response.ok) {
            throw new Error(`Failed to delete document ${docId}`)
          }
        })
      )

      // 削除成功後、選択をクリアしてデータを再取得
      setSelectedDocuments([])
      refetch()

      console.log(`✅ ${selectedDocuments.length}件の議事録を削除しました`)
    } catch (error) {
      console.error('Delete failed:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleUpdateDocument = async (docId: string, updates: { folderId?: string | null; tags?: string[] }) => {
    console.log('🔄 Starting document update:', { docId, updates })
    console.log('🔍 Current user state:', { firebaseUser: !!firebaseUser, user })
    
    // オプティミスティック更新: UIを先に更新してユーザーエクスペリエンスを向上
    if (updates.folderId !== undefined) {
      queryClient.setQueryData(['documents-infinite', filters], (oldData: any) => {
        if (!oldData) return oldData
        
        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((doc: any) => 
            doc.id === docId ? { ...doc, folderId: updates.folderId } : doc
          )
        }))
        
        return { ...oldData, pages: newPages }
      })
    }
    
    try {
      const token = await firebaseUser?.getIdToken()
      if (!token) {
        console.error('❌ No authentication token available')
        return
      }

      console.log('🔑 Token obtained successfully')
      console.log('🌐 Making API request to:', `${API_BASE}/v1/organizations/${user.orgId}/documents/${docId}`)
      console.log('📤 Request body:', JSON.stringify(updates, null, 2))

      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/documents/${docId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      })

      console.log('📡 Response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        })
        
        // オプティミスティック更新を元に戻す
        await queryClient.invalidateQueries({ queryKey: ['documents-infinite'] })
        
        throw new Error(`Update request failed: ${response.status} - ${errorText}`)
      }

      const responseData = await response.json()
      console.log('📥 Response data:', responseData)
      console.log('📥 Updated folderId:', responseData.folderId)

      // 成功時はサーバーからの正確なデータでキャッシュを更新
      console.log('📂 Refreshing data to ensure consistency...')
      
      // 1. キャッシュを完全にクリア
      queryClient.removeQueries({ queryKey: ['documents-infinite'] })
      queryClient.removeQueries({ queryKey: ['folders'] })
      
      // 2. 新しいデータを取得
      await queryClient.invalidateQueries({ queryKey: ['folders'] })
      await queryClient.invalidateQueries({ queryKey: ['documents-infinite'] })
      
      // 3. 強制的に再取得
      await refetch()
      console.log('🔄 Complete cache refresh completed')

      console.log('✅ Document updated successfully:', { docId, updates })
      
      // サクセスメッセージを表示
      if (updates.folderId !== undefined) {
        const folderName = foldersData?.folders?.find(f => f.id === updates.folderId)?.name || 'フォルダなし'
        console.log(`✅ フォルダを「${folderName}」に変更しました`)
      }
      
    } catch (error) {
      console.error('❌ Document update failed:', error)
    }
  }

  const handleBulkFolderChange = async (folderId: string | null) => {
    setShowBulkFolderMenu(false)
    try {
      // 選択された全ドキュメントのフォルダを変更（並列処理）
      await Promise.all(
        selectedDocuments.map(docId => handleUpdateDocument(docId, { folderId }))
      )
      setSelectedDocuments([])
      await queryClient.invalidateQueries({ queryKey: ['documents-infinite'] })
      await queryClient.invalidateQueries({ queryKey: ['folders'] })
    } catch (error) {
      console.error('❌ Bulk folder change failed:', error)
    }
  }

  const handleBulkShareChange = async (shareType: 'private' | 'organization' | 'specific' | 'public') => {
    console.log('🔗 Bulk changing share type:', { count: selectedDocuments.length, shareType })
    setShowBulkShareMenu(false)
    
    const shareTypeLabel = {
      'private': '非公開',
      'organization': '組織内全員',
      'specific': '特定のユーザー',
      'public': 'URLを知っている人'
    }[shareType]
    
    try {
      // 選択された全ドキュメントに対して共有設定を変更（並列処理）
      await Promise.all(
        selectedDocuments.map(docId => handleShareChange(docId, shareType))
      )
      
      console.log(`✅ ${selectedDocuments.length}件の議事録を「${shareTypeLabel}」に変更しました`)
      setSelectedDocuments([])
    } catch (error) {
      console.error('❌ Bulk share change failed:', error)
    }
  }

  const handleShareChange = async (docId: string, shareType: 'private' | 'organization' | 'specific' | 'public') => {
    console.log('🔗 Changing share type:', { docId, shareType })
    
    try {
      const token = await firebaseUser?.getIdToken()
      if (!token) {
        console.error('❌ No authentication token')
        return
      }

      // まず既存の共有をすべて削除
      console.log('🗑️ Deleting existing shares...')
      const sharesResponse = await fetch(`${API_BASE}/v1/shares?resourceType=document&resourceId=${docId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (sharesResponse.ok) {
        const shares = await sharesResponse.json()
        console.log('📋 Found existing shares:', shares.length)
        
        // すべての共有を削除
        for (const share of shares) {
          console.log('🗑️ Deleting share:', share.id)
          const deleteResponse = await fetch(`${API_BASE}/v1/shares/${share.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          
          if (!deleteResponse.ok) {
            console.error('❌ Failed to delete share:', share.id)
          }
        }
      }

      // privateでない場合は新しい共有を作成
      if (shareType !== 'private') {
        let recipients: any[] = []
        
        if (shareType === 'organization') {
          recipients = [{ type: 'user', identifier: `org:${user.orgId}` }]
        } else if (shareType === 'public') {
          recipients = [{ type: 'public', identifier: 'public' }]
        } else if (shareType === 'specific') {
          // 特定ユーザーの場合は、ダイアログで入力が必要
          console.log('⚠️ 特定ユーザーへの共有は、議事録詳細ページから設定してください')
          return
        }

        console.log('➕ Creating new share:', { shareType, recipients })
        const createResponse = await fetch(`${API_BASE}/v1/shares`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            resourceType: 'document',
            resourceId: docId,
            recipients,
            settings: {
              requireAuth: shareType !== 'public',
              allowDownload: true
            }
          })
        })

        if (!createResponse.ok) {
          const errorText = await createResponse.text()
          console.error('❌ Failed to create share:', errorText)
          throw new Error(`共有の作成に失敗しました: ${createResponse.status}`)
        }

        const newShare = await createResponse.json()
        console.log('✅ Share created:', newShare)
      }

      // キャッシュを更新
      console.log('🔄 Refreshing cache...')
      await queryClient.invalidateQueries({ queryKey: ['documents-infinite'] })
      await refetch()
      
      console.log('✅ Share type changed successfully')
    } catch (error) {
      console.error('❌ Share change failed:', error)
    }
  }

  const handleFolderSelect = (folderId: string | null) => {
    console.log('📂 Folder selected:', { folderId, currentFilters: filters })
    setSelectedFolderId(folderId)

    // フィルターを更新
    setFilters(prev => {
      let newFilters = { ...prev }

      if (folderId === 'bookmarks') {
        // ブックマークフィルター
        newFilters = {
          ...prev,
          folderId: undefined,
          bookmarked: true
        }
      } else {
        // 通常のフォルダーフィルター
        newFilters = {
          ...prev,
          folderId: folderId || undefined,
          bookmarked: undefined
        }
      }

      console.log('🔍 Setting new filters:', { old: prev, new: newFilters })
      return newFilters
    })
  }

  return (
    <Layout 
      title="議事録"
      showSidebar={true}
      selectedFolderId={selectedFolderId}
      onFolderSelect={handleFolderSelect}
    >
      <div className="flex-1">
          {/* セクションヘッダー（フォルダ/ブックマーク絞り込み中のみ表示） */}
          {selectedFolderId && (
          <div className="section-header">
            <div className="flex items-center gap-3">
              {selectedFolderId === 'bookmarks' && (
                <button
                  className="filter-chip active"
                  onClick={() => handleFolderSelect(null)}
                  title="絞り込みを解除"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  ブックマーク済み
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {selectedFolderId && selectedFolderId !== 'bookmarks' && foldersData?.folders && (
                <>
                  <button
                    className="filter-chip active"
                    onClick={() => handleFolderSelect(null)}
                    title="絞り込みを解除"
                  >
                    フォルダ: {foldersData.folders.find(f => f.id === selectedFolderId)?.name || '不明なフォルダ'}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  {/* フォルダの共有状況の確認・変更（現在の状態をアイコン+ラベルで表示） */}
                  {(() => {
                    const folder = foldersData.folders.find((f: any) => f.id === selectedFolderId) as any
                    if (!folder) return null
                    const restricted = folder.visibility === 'restricted'
                    const memberCount = restricted
                      ? (folder.acl?.owners?.length || 0) + (folder.acl?.editors?.length || 0) + (folder.acl?.viewers?.length || 0)
                      : 0
                    return (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => setShowFolderAcl(true)}
                        title={`このフォルダの共有状況を確認・変更（現在: ${restricted ? `限定メンバー${memberCount ? ` ${memberCount}人` : ''}` : '組織全体に公開'}）`}
                      >
                        {restricted ? (
                          <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                            <circle cx="9" cy="7" r="4" />
                          </svg>
                        ) : (
                          <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4" />
                          </svg>
                        )}
                        {restricted ? `限定メンバー${memberCount ? `（${memberCount}人）` : ''}` : '組織全体に公開'}
                      </button>
                    )
                  })()}
                </>
              )}
            </div>
          </div>
          )}

          {/* フォルダ共有状況モーダル */}
          {showFolderAcl && selectedFolderId && selectedFolderId !== 'bookmarks' && (() => {
            const folder = foldersData?.folders?.find((f: any) => f.id === selectedFolderId) as any
            if (!folder) return null
            return (
              <FolderAclModal
                folderId={folder.id}
                folderName={folder.name}
                currentAcl={folder.acl}
                currentVisibility={folder.visibility}
                onClose={() => setShowFolderAcl(false)}
                onSuccess={async () => {
                  setShowFolderAcl(false)
                  await queryClient.invalidateQueries({ queryKey: ['folders'] })
                }}
              />
            )
          })()}

          {/* 選択状態のヘッダー */}
          {selectedDocuments.length > 0 && (
            <div className="bulk-actions">
              <span className="selection-count">
                {selectedDocuments.length}件の議事録が選択されています
              </span>
              <div className="flex items-center gap-2 ml-auto">
                {/* 一括フォルダ変更ドロップダウン */}
                <div className="relative">
                  <button
                    onClick={() => { setShowBulkFolderMenu(!showBulkFolderMenu); setShowBulkShareMenu(false) }}
                    className="btn btn-outline btn-sm"
                  >
                    <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    フォルダを変更
                  </button>
                  {showBulkFolderMenu && (
                    <div className="dropdown-menu right open" style={{ maxHeight: 280, overflowY: 'auto', minWidth: 200 }}>
                      <button
                        onClick={() => handleBulkFolderChange(null)}
                        className="dropdown-item flex items-center gap-2"
                      >
                        <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: 'var(--color-border-subtle)' }} />
                        フォルダなし
                      </button>
                      {(foldersData?.folders || []).map((folder: any) => (
                        <button
                          key={folder.id}
                          onClick={() => handleBulkFolderChange(folder.id)}
                          className="dropdown-item flex items-center gap-2"
                        >
                          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: folder.color || 'var(--color-accent)' }} />
                          <span className="truncate">{folder.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 一括共有変更ドロップダウン */}
                <div className="relative">
                  <button
                    onClick={() => setShowBulkShareMenu(!showBulkShareMenu)}
                    className="btn btn-primary btn-sm"
                  >
                    <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    共有設定を変更
                  </button>

                  {showBulkShareMenu && (
                    <div className="dropdown-menu right open">
                      <button
                        onClick={() => handleBulkShareChange('private')}
                        className="dropdown-item"
                      >
                        🔒 非公開
                      </button>
                      <button
                        onClick={() => handleBulkShareChange('organization')}
                        className="dropdown-item"
                      >
                        👥 組織内全員
                      </button>
                      <button
                        onClick={() => handleBulkShareChange('public')}
                        className="dropdown-item"
                      >
                        🌐 URLを知っている人
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setSelectedDocuments([])}
                  className="btn btn-ghost btn-sm"
                >
                  選択解除
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                  className="btn btn-danger btn-sm"
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner sm" aria-hidden="true" />
                      削除中...
                    </>
                  ) : (
                    <>
                      <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      削除
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* フィルタ */}
          <DocumentFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleFiltersReset}
            totalCount={totalDocuments}
          />

          {/* コンテンツ */}
          <div className="mt-4">
            {isLoading ? (
              <div className="flex justify-center items-center py-16 gap-3">
                <span className="spinner lg" aria-hidden="true" />
                <span className="text-secondary">データを読み込み中...</span>
              </div>
            ) : error ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="empty-state-title">読み込みエラー</h3>
                <p className="empty-state-desc">
                  データの読み込みに失敗しました。再試行してください。
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-primary"
                >
                  再試行
                </button>
              </div>
            ) : allDocuments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="empty-state-title">
                  議事録がありません
                </h3>
                <p className="empty-state-desc">
                  まだ議事録が作成されていないか、フィルタ条件に一致するものがありません。
                </p>
                <button
                  onClick={() => navigate('/create')}
                  className="btn btn-primary"
                >
                  新規議事録
                </button>
              </div>
            ) : (
              <div className="table-container">
                <DocumentTable
                  documents={allDocuments}
                  folders={foldersData?.folders || []}
                  onBookmark={handleBookmark}
                  onClick={handleDocumentClick}
                  onSort={handleSort}
                  sortField={filters.sortBy}
                  sortOrder={filters.sortOrder}
                  selectedDocuments={selectedDocuments}
                  onDocumentSelect={handleDocumentSelect}
                  onSelectAll={handleSelectAll}
                  onUpdateDocument={handleUpdateDocument}
                  onShareChange={handleShareChange}
                />
              </div>
            )}

            {/* 無限スクロールローディング */}
            {isFetchingNextPage && (
              <div className="flex justify-center items-center py-8 gap-2">
                <span className="spinner" aria-hidden="true" />
                <span className="text-sm text-secondary">読み込み中...</span>
              </div>
            )}

            {/* 無限スクロール用の番兵 */}
            <div ref={loadMoreSentinelRef} aria-hidden="true" />

            {/* 件数表示 */}
            {!isLoading && allDocuments.length > 0 && (
              <div className="mt-6 text-center">
                <p className="text-sm text-tertiary">
                  {allDocuments.length} / {totalDocuments} 件表示
                  {hasNextPage && (
                    <span className="ml-2 text-accent">
                      （スクロールして続きを読み込み）
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
    </Layout>
  )
}